import {
  ButtonIcon,
  FlexContainer,
  useClickOutside,
  useDialog,
} from "@/lib/main";
import { useEffect, useRef, type CSSProperties } from "react";

type Props = {
  maxContentHeight?: CSSProperties["maxHeight"];
  maxContentWidth?: CSSProperties["maxWidth"];
};

export default function Dialog({ maxContentHeight, maxContentWidth }: Props) {
  const { showDialog, content, dialogRef, closeDialog } = useDialog();
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // * close if user clicks outside container
  const handleClickOutside = useClickOutside(contentContainerRef, () => {
    if (closeDialog) closeDialog();
  });

  useEffect(() => {
    if (showDialog) {
      dialogRef?.current?.showModal();
      buttonRef.current?.blur();
    }
  }, [dialogRef, showDialog]);

  useEffect(() => {
    if (!showDialog) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeDialog) {
        closeDialog();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showDialog, closeDialog]);

  const dialogStyle: CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    backgroundColor: "rgba(0, 0, 0, 0.8)",
    border: "none",

    padding: "var(--page-padding-vertical) var(--page-padding-horizontal)",

    height: "100dvh",
    maxHeight: "100dvh",
    width: "100dvw",
    maxWidth: "100dvw",
  };

  const contentContainerStyle: CSSProperties = {
    backgroundColor: "var(--color-background-frame)",
    borderRadius: "var(--border-radius-lg)",

    padding: "var(--spacing-xl)",

    width: "100%",
    maxWidth: maxContentWidth ?? "60ch",
    maxHeight:
      maxContentHeight ??
      "calc(100dvh - var(--navbar-height) - var(--tabbar-height))",
    overflowY: "auto",
  };

  return (
    <>
      {content ? (
        <dialog
          className={
            showDialog ? "animation__fade--in" : "animation__fade--out"
          }
          onClick={handleClickOutside}
          onCancel={(e) => {
            // * Prevent the browser from closing the dialog natively (e.g. Android
            // * back button, iOS swipe-back). We handle close ourselves so React
            // * state stays in sync and disabled-button locks never occur.
            e.preventDefault();
            closeDialog();
          }}
          ref={dialogRef}
          style={dialogStyle}
        >
          <div ref={contentContainerRef} style={contentContainerStyle}>
            <FlexContainer justify="end" fullWidth>
              <ButtonIcon
                buttonRef={buttonRef}
                iconName="close"
                onClick={closeDialog}
                size="md"
              />
            </FlexContainer>
            {content}
          </div>
        </dialog>
      ) : null}
    </>
  );
}
